import React, { useContext, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  FaBars,
  FaTimes,
  FaUser,
  FaMusic,
  FaQrcode,
  FaSignOutAlt,
  FaSignInAlt,
  FaUserPlus,
  FaChevronRight,
  FaHeadset,
  FaFileContract,
  FaShieldAlt,
} from "react-icons/fa";
import { useLocation } from "react-router-dom";
import api from "../api";
import "./Header.css";

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(true);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fetchCount = async () => {
      if (!isAuthenticated) return;
      try {
        const { data } = await api.get("/orders/new");
        setPendingCount(data.length);
      } catch (err) {
        console.error("Error fetching new orders", err);
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30_000);
    return () => clearInterval(t);
  }, [isAuthenticated]);

  // Fetch accepting requests status
  useEffect(() => {
    if (!isAuthenticated || !user?.slug) return;
    api.get(`/users/${user.slug}`).then((r) => {
      setIsAccepting(r.data.isAcceptingRequests !== false);
    }).catch(() => {});
  }, [isAuthenticated, user?.slug]);

  const toggleStatus = useCallback(async (accepting: boolean) => {
    if (!user?.slug) return;
    setStatusLoading(true);
    try {
      const { data } = await api.patch(`/users/${user.slug}/accepting-requests`, { isAcceptingRequests: accepting });
      setIsAccepting(data.isAcceptingRequests);
      setShowOfflineModal(false);
    } catch (err) {
      console.error("Failed to update status", err);
    } finally {
      setStatusLoading(false);
    }
  }, [user?.slug]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      document.body.style.overflow = isMenuOpen ? "hidden" : "";
    }
  }, [isMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);
  const handleLogout = () => {
    logout();
    navigate("/");
    closeMenu();
  };

  const hideAllLinks =
    (location.pathname.includes("/dj/") &&
      location.pathname.includes("/request")) ||
    location.pathname === "/success" ||
    location.pathname === "/free-success";

  return (
    <header className="sncf-header">
      <div className="sncf-brand">
        <Link
          to={isAuthenticated ? "/requests" : "/"}
          className="sncf-logo-link"
          onClick={closeMenu}
        >
          <span className="sncf-logo-box">
            <img src="/r.png" alt="Requesta" className="sncf-logo-img" />
          </span>
          <span className="sncf-logo-text">REQUEST APP</span>
        </Link>
      </div>
      {!hideAllLinks && (
        <>
          {/* ── Desktop nav ──────────────────────────────── */}
          <nav className="sncf-desktop-nav">
            {!isAuthenticated ? (
              <>
                <Link to="/login">Login</Link>
                <Link to="/register" className="sncf-btn-signup">
                  Create your Account
                </Link>
              </>
            ) : (
              <>
                {/* Desktop status toggle */}
                <div
                  onClick={() => {
                    if (statusLoading) return;
                    if (isAccepting) setShowOfflineModal(true);
                    else toggleStatus(true);
                  }}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    marginLeft: 20, cursor: "pointer",
                  }}
                >
                  <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem", letterSpacing: "-0.2px" }}>
                    {isAccepting ? "Live" : "Offline"}
                  </span>
                  <div style={{
                    width: 44, height: 26, borderRadius: 13,
                    background: isAccepting ? "#fff" : "rgba(255,255,255,0.3)",
                    position: "relative",
                    transition: "background 0.2s ease",
                  }}>
                    <div style={{
                      width: 22, height: 22,
                      borderRadius: "50%",
                      background: isAccepting ? "#22c55e" : "#fff",
                      position: "absolute",
                      top: 2,
                      left: isAccepting ? 20 : 2,
                      transition: "left 0.2s ease, background 0.2s ease",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }} />
                  </div>
                </div>
                <Link to="/requests" className="sncf-requests-link">
                  Requests
                  {pendingCount > 0 && (
                    <span className="sncf-badge">{pendingCount}</span>
                  )}
                </Link>
                <Link to="/profile">My Profile</Link>
                <Link to={`/qr/${user?.slug}`}>My QR</Link>
                <button onClick={handleLogout} className="sncf-btn-logout">
                  Logout
                </button>
              </>
            )}
          </nav>

          {/* ── Mobile burger ────────────────────────────── */}
          <button
            className={`sncf-burger${isMenuOpen ? " sncf-burger--hidden" : ""}`}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setIsMenuOpen((v) => !v)}
          >
            <FaBars />
          </button>

          {/* ── Mobile overlay + panel ───────────────────── */}
          <div
            className={`sncf-overlay ${isMenuOpen ? "sncf-overlay--open" : ""}`}
            onClick={(e) => {
              if (e.target === e.currentTarget) closeMenu();
            }}
          >
            <div className="sncf-panel">
              {/* Panel header */}
              <div className="sncf-panel-header">
                <h2 className="sncf-menu-title">Menu</h2>
                <button
                  className="sncf-panel-close"
                  onClick={closeMenu}
                  aria-label="Close menu"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Menu items */}
              <nav className="sncf-menu-list">
                <div className="sncf-group">
                  {!isAuthenticated ? (
                    <>
                      <Link
                        to="/login"
                        className="sncf-item"
                        onClick={closeMenu}
                      >
                        <span className="sncf-item-icon sncf-item-icon--blue">
                          <FaSignInAlt />
                        </span>
                        Login
                        <FaChevronRight className="sncf-item-arrow" />
                      </Link>
                      <Link
                        to="/register"
                        className="sncf-item sncf-item--cta"
                        onClick={closeMenu}
                      >
                        <span className="sncf-item-icon sncf-item-icon--green">
                          <FaUserPlus />
                        </span>
                        Create your Account
                        <FaChevronRight className="sncf-item-arrow" />
                      </Link>
                    </>
                  ) : (
                    <>
                      {/* Status Module */}
                      <span
                        className="sncf-item"
                        onClick={() => {
                          if (statusLoading) return;
                          if (isAccepting) setShowOfflineModal(true);
                          else toggleStatus(true);
                        }}
                      >
                        <span className={`sncf-item-icon ${isAccepting ? "sncf-item-icon--green" : "sncf-item-icon--gray"}`}
                          style={!isAccepting ? { filter: "grayscale(1) opacity(0.5)" } : undefined}
                        >
                          <span style={{ fontSize: "1.1rem" }}>💰</span>
                        </span>
                        <span style={{ flex: 1 }}>
                          {isAccepting ? "Live" : "Offline"}
                          <div style={{ fontSize: "0.72rem", color: isAccepting ? "#86efac" : "#9ca3af", fontWeight: 400 }}>
                            {isAccepting ? "Ready for Requests & Tips" : "Requests Off. Tips Only"}
                          </div>
                        </span>
                        <div style={{
                          width: 44, height: 26, borderRadius: 13,
                          background: isAccepting ? "#22c55e" : "rgba(255,255,255,0.15)",
                          position: "relative",
                          transition: "background 0.2s ease",
                          flexShrink: 0,
                        }}>
                          <div style={{
                            width: 22, height: 22,
                            borderRadius: "50%",
                            background: "#fff",
                            position: "absolute",
                            top: 2,
                            left: isAccepting ? 20 : 2,
                            transition: "left 0.2s ease",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          }} />
                        </div>
                      </span>

                      <div className="sncf-sep" aria-hidden="true" />

                      <Link
                        to="/requests"
                        className="sncf-item"
                        onClick={closeMenu}
                      >
                        <span className="sncf-item-icon sncf-item-icon--green">
                          <FaMusic />
                        </span>
                        Requests
                        {pendingCount > 0 && (
                          <span className="sncf-badge">{pendingCount}</span>
                        )}
                        <FaChevronRight className="sncf-item-arrow" />
                      </Link>
                      <Link
                        to="/profile"
                        className="sncf-item"
                        onClick={closeMenu}
                      >
                        <span className="sncf-item-icon sncf-item-icon--blue">
                          <FaUser />
                        </span>
                        My Profile
                        <FaChevronRight className="sncf-item-arrow" />
                      </Link>
                      <Link
                        to={`/qr/${user?.slug}`}
                        className="sncf-item"
                        onClick={closeMenu}
                      >
                        <span className="sncf-item-icon sncf-item-icon--purple">
                          <FaQrcode />
                        </span>
                        My QR
                        <FaChevronRight className="sncf-item-arrow" />
                      </Link>
                    </>
                  )}
                </div>

                <div className="sncf-sep" aria-hidden="true" />

                <div className="sncf-group">
                  <span
                    className="sncf-item"
                    onClick={() => {
                      closeMenu();
                      window.dispatchEvent(new CustomEvent("open-chatbot"));
                    }}
                  >
                    <span className="sncf-item-icon sncf-item-icon--gray">
                      <FaHeadset />
                    </span>
                    Support / Help
                    <FaChevronRight className="sncf-item-arrow" />
                  </span>
                  <Link to="/terms" className="sncf-item" onClick={closeMenu}>
                    <span className="sncf-item-icon sncf-item-icon--gray"><FaFileContract /></span>
                    Terms & Conditions
                    <FaChevronRight className="sncf-item-arrow" />
                  </Link>
                  <Link to="/privacy" className="sncf-item" onClick={closeMenu}>
                    <span className="sncf-item-icon sncf-item-icon--gray"><FaShieldAlt /></span>
                    Privacy Policy
                    <FaChevronRight className="sncf-item-arrow" />
                  </Link>
                </div>
              </nav>

              {/* Bottom action */}
              <div className="sncf-panel-footer">
                {!isAuthenticated ? (
                  <Link
                    to="/login"
                    className="sncf-bottom-btn sncf-bottom-btn--light"
                    onClick={closeMenu}
                  >
                    <FaUser className="sncf-bottom-icon" />
                    <span>Sign In</span>
                  </Link>
                ) : (
                  <button
                    className="sncf-bottom-btn sncf-bottom-btn--danger"
                    onClick={handleLogout}
                  >
                    <FaSignOutAlt className="sncf-bottom-icon" />
                    Logout
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {/* Go Offline confirmation modal */}
      {showOfflineModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000,
          }}
          onClick={() => setShowOfflineModal(false)}
        >
          <div
            style={{
              background: "#111827", color: "#fff", border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 16, padding: 24, width: "min(92vw, 380px)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontWeight: 700, fontSize: "1.1rem", margin: "0 0 10px", textAlign: "center" }}>
              Go Offline?
            </h3>
            <p style={{ color: "#9ca3af", fontSize: "0.9rem", lineHeight: 1.5, marginBottom: 20, textAlign: "center" }}>
              People won't be able to send requests. Your profile will still accept tips.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => toggleStatus(false)}
                disabled={statusLoading}
                style={{
                  flex: 1, padding: "12px 14px", borderRadius: 10,
                  background: "rgba(255,255,255,0.06)", color: "#9ca3af",
                  border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", fontWeight: 700, fontSize: "0.92rem",
                }}
              >
                {statusLoading ? "..." : "Go Offline"}
              </button>
              <button
                onClick={() => { setShowOfflineModal(false); }}
                style={{
                  flex: 1, padding: "12px 14px", borderRadius: 10,
                  background: "linear-gradient(135deg, #22c55e, #16a34a)",
                  color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: "0.92rem",
                }}
              >
                Stay Live
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
